import { useState, type FormEvent } from 'react'
import { supabase } from '../lib/supabase'
import { t } from '../lib/i18n'
import { LanguageSwitch } from '../components/LanguageSwitch'

/**
 * Email and password. No sign-up link, deliberately: sign-ups are switched off
 * in the Supabase dashboard and new logins are created DISABLED (migration 003).
 * An administrator activates them. A field app that lets anyone create an
 * account is a field app that will one day contain someone else's households.
 */
export function LoginScreen({ onForgot }: { onForgot: () => void }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)

    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    })

    if (error) {
      // Supabase returns "Invalid login credentials" for a wrong password AND
      // for an address that does not exist. That is deliberate on their part —
      // distinguishing them would let anyone test which staff emails are real.
      // The message is rewritten here to be useful without leaking that.
      setError(
        error.message === 'Invalid login credentials'
          ? t('login.wrong')
          : error.message,
      )
      setBusy(false)
      return
    }
    // On success the auth listener in App.tsx swaps the screen. Nothing to do.
  }

  return (
    <div className="screen">
      <div className="appbar">
        <h1>{t('app.title')}</h1>
        <LanguageSwitch compact />
      </div>

      <div className="center-card">
        <form className="card" onSubmit={onSubmit}>
          <h2 style={{ marginTop: 0 }}>{t('login.heading')}</h2>
          

          <label htmlFor="email">{t('login.email')}</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            required
          />

          <label htmlFor="password">{t('login.password')}</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />

          {error && (
            <div className="alert alert-error" role="alert">
              {error}
            </div>
          )}

          <div style={{ marginTop: 18 }}>
            <button className="btn-primary" type="submit" disabled={busy || !email || !password}>
              {busy ? t('login.working') : t('login.submit')}
            </button>
          </div>

          <div style={{ marginTop: 16 }}>
            {/* type="button" matters: inside a <form>, a bare <button> submits
                it, so this would have tried to sign in instead. */}
            <button type="button" className="btn-quiet" style={{ padding: '10px 0', minHeight: 44 }} onClick={onForgot}>
              {t('login.forgot')}
            </button>
          </div>

          <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>
            {t('login.noSignup')}
          </p>
        </form>
      </div>
    </div>
  )
}
